import React, { useState, useEffect, useRef, useId, useMemo, useCallback } from 'react';
import Chart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { Box, Typography } from '@mui/material';
import VerticalSlider from '../item/slider_vertical'
import Progress from '../item/progress'
import TooltipRiskScatter from './tooltip_risk_scatter';

export default function ScatterRisk(props) {
    const [series, setSeries] = useState([]);
    const [seriesXMax, setSeriesXMax] = useState([]);
    const [seriesYMax, setSeriesYMax] = useState([]);
    const [value, setValue] = useState([0, props.distance]);
    const [isSlider, setIsSlider] = useState(false);
    const [isNoData, setIsNoData] = useState(false);

    const initializedRef = useRef(false);
    const chartId = useId();

    /**
     * Returns maximum of two numbers
     * Used for Array.reduce
     */
    const aryMax = function (a, b) { return Math.max(a, b); }

    const getSeries = (chartData) => {
        setSeries(props.getSeries(chartData, isSlider, value));
    };

    /**
     * Generate chart series from scatter data
     * - Determines if slider should be enabled
     * - Rebuilds scatter series based on current slider value
     */
    const setChartData = (chartData) => {
        const isOver = props.distance < chartData.length;
        setIsSlider(isOver);
        getSeries(chartData)
        // const newSeries = props.getSeries(chartData, isOver, value);
        // setSeries(newSeries);
    };

    /**
     * Mark all keys in isReadyKey as ready
     * Used after data loading or update completion
     */
    const loadingEnd = () => {
        props.setReady(prev => {
            const updated = { ...prev };
            props.isReadyKey.forEach(key => {
                if (key in updated) {
                    updated[key] = true;
                }
            });
            return updated;
        });
    };

    // データ更新が完了したらローディング終了
    useEffect(() => {
        loadingEnd()
    }, [series]);

    const roundUpNice = (num) => {
        if (num <= 0) return 0;
        const digits = Math.floor(Math.log10(num));
        const base = Math.pow(10, digits);
        return Math.ceil(num / base) * base;
    };

    useEffect(() => {
        if (!props.scatterData || props.scatterData.length === 0) {
            setIsNoData(true);
            loadingEnd();
            return;
        }
        if (props.scatterData && props.scatterData.length > 0) {

            setChartData(props.scatterData)
        }
        setIsNoData(false);
        loadingEnd();
    }, [props.dateArray, props.scatterData]);

    useEffect(() => {
        if (props.scatterData === null || props.scatterData.length === 0) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.scatterData && props.scatterData.length > 0) {
            setChartData(props.scatterData)
        }
        setIsNoData(false)
        loadingEnd()
        return () => {
            ApexCharts.exec(chartId, 'destroy'); // 明示的に破棄
        };
    }, []);

    useEffect(() => {
        if (!initializedRef.current && props.scatterData?.length > 0) {
            const x_ary = props.x_ary(props.scatterData);
            const y_ary = props.y_ary(props.scatterData);
            const x_max = x_ary.reduce(aryMax);
            const y_max = y_ary.reduce(aryMax);
            const xStep = roundUpNice(x_max);
            const yStep = roundUpNice(y_max);
            setSeriesXMax(xStep);
            setSeriesYMax(yStep);
            initializedRef.current = true;
        }
    }, [props.scatterData]);

    // useEffect(() => {
    //     if (props.scatterData?.length > 0) {
    //         setChartData(props.scatterData);
    //     }
    // }, [value, props.scatterData, setChartData]);

    const options = useMemo(() => {
        const getMarkerSize = () => {
            if (series.length === 0) {
                return props.sizePoint || 2;
            }
            return series.map(s => s.marker?.size ?? props.sizePoint ?? 2);
        }
        return {
            chart: {
                type: "scatter",
                background: "#000",
                zoom: {
                    enabled: false,
                    type: 'xy'
                },
                toolbar: {
                    show: false
                },
                events: {
                    dataPointSelection: (event, chartContext, config) => {
                        props.setReady(prev => {
                            const updated = Object.fromEntries(
                                Object.keys(prev).map(key => [key, false])
                            );
                            return updated;
                        });
                        const seriesIndex = config.seriesIndex;
                        const dataPointIndex = config.dataPointIndex;
                        if (seriesIndex === -1 || dataPointIndex === -1) return;
                        const point = chartContext.w.config.series[seriesIndex].data[dataPointIndex];
                        if (!point?.meta) return;
                        props.scatterClickEvent(point?.meta);
                    },
                    dataPointMouseEnter: (event, chartContext, config) => {
                        const { seriesIndex, dataPointIndex } = config;
                        if (seriesIndex === -1 || dataPointIndex === -1) {
                            props.setTooltipInfo(null);
                            return;
                        }
                        const point = chartContext.w.config.series[seriesIndex].data[dataPointIndex];
                        if (!point?.meta) {
                            props.setTooltipInfo(null);
                            return;
                        }
                        props.setTooltipInfo({
                            x: event.clientX,
                            y: event.clientY,
                            data: point?.meta,
                            tooltipKey: props.tooltipKey
                        });
                    },
                    mouseLeave: () => {
                        props.setTooltipInfo(null);
                    },
                    mouseMove: (event, chartContext, config) => {
                        const { seriesIndex, dataPointIndex } = config;
                        if (seriesIndex === -1 || dataPointIndex === -1) {
                            props.setTooltipInfo(null);
                        }
                    },
                }
            },
            theme: {
                mode: "dark",
            },
            legend: {
                show: false,
            },
            xaxis: {
                show: true,
                min: 0,
                max: seriesXMax,
                tickAmount: 4,
                labels: {
                    style: { colors: '#fff' },
                },
                title: {
                    text: props.textX,
                    style: {
                        color: '#FFFFFF'
                    }
                },
                crosshairs: {
                    show: false
                }
            },
            yaxis: {
                show: true,
                min: 0,
                max: seriesYMax,
                tickAmount: 4,
                title: {
                    text: props.textY,
                    offsetX: 4,
                    style: {
                        color: '#FFFFFF'
                    }
                },
                labels: {
                    style: {
                        colors: "#ffffff",
                    },
                },
                crosshairs: {
                    show: false
                }
            },
            markers: {
                size: getMarkerSize(),
                strokeWidth: 0,
                strokeColor: "transparent",
            },
            dataLabels: {
                enabled: props.isShowDataLabel,
                textAnchor: 'middle',
                offsetY: 5,
                style: {
                    fontSize: '10px',
                    colors: ['#ffffff']
                },
                background: {
                    enabled: false
                },
                formatter: function (val, opt) {
                    return opt.w.config.series[opt.seriesIndex].name
                },
            },
            annotations: {
                xaxis: [
                    {
                        x: props.maxValX,
                        strokeDashArray: 4,
                        label: {
                            text: `${props.maxValX} ${props?.isShowLabelMaxXMm === false ? '' : 'mm'}`,
                            borderWidth: 0,
                            style: {
                                color: "#ffffff",
                                background: "transparent",
                                borderColor: "transparent",
                            },
                        },
                    },
                ],
                yaxis: [
                    {
                        y: props.maxValY,
                        strokeDashArray: 4,
                        label: {
                            text: `${props.maxValY}mm`,
                            borderWidth: 0,
                            style: {
                                color: "#ffffff",
                                background: "transparent",
                                borderColor: "transparent",
                            },
                        },
                    },
                ],
            },
            grid: {
                borderColor: "transparent",
            },
            tooltip: {
                intersect: true,
                enabled: false,
                shared: false
            }
        }
    }, [props.scatterData, series, seriesXMax, seriesYMax]);

    useEffect(() => {
        if (props.scatterData !== null && props.scatterData !== undefined) {
            ApexCharts.exec(chartId,
                "updateSeries", series);
        }
    }, [props.scatterData]);

    return (
        <Box sx={{ display: 'flex', height: props.height, overflow: 'hidden' }}>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {!props?.isReadyFlg ? (
                    <Progress />
                ) : isNoData ? (
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'white' }}>
                        対象データがありません
                    </Typography>
                ) : (
                    <Box sx={{ position: 'relative', flex: 1, overflow: 'hidden', height: "100%" }}>
                        <Chart
                            options={options}
                            series={series}
                            type="scatter"
                            height="100%"
                            width="100%"
                            style={{ visibility: props?.isReadyFlg ? 'visible' : 'hidden' }}
                        />
                        {props?.tooltipInfo && props?.tooltipInfo?.tooltipKey === props?.tooltipKey && (
                            <Box
                                sx={{
                                    position: 'fixed',
                                    left: Math.min(window.innerWidth - 160, props.tooltipInfo.x),
                                    top: Math.min(window.innerHeight - 100, props.tooltipInfo.y) + 10,
                                    transform: 'translateX(-50%)',
                                    maxWidth: 300,
                                    whiteSpace: props.whiteSpace || 'normal',
                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                    color: '#000',
                                    padding: '8px 12px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    pointerEvents: 'none',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    zIndex: 9999,
                                    transition: 'all 0.15s ease-out',
                                    opacity: props?.tooltipInfo ? 1 : 0,
                                }}
                            >
                                <TooltipRiskScatter
                                    tooltipInfo={props?.tooltipInfo}
                                    tooltipKey={props?.tooltipInfo?.tooltipKey}
                                    renderBody={props.renderBody}
                                />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
            {isSlider && (
                <Box sx={{ width: '20px', marginLeft: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <VerticalSlider
                        data={props.scatterData}
                        value={value}
                        setValue={setValue}
                        distance={props.distance}
                        selectCitys={props.selectCitys}
                    />
                </Box>
            )}
        </Box>
    );
}








